import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useActivePage, useNotifications } from '@toolpad/core';
import { useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router';
import { ExpandMore } from '@mui/icons-material';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Card,
    CardContent,
    CardHeader,
    Stack,
    Typography,
} from '@mui/material';

import { QueryRenderer } from '@barboxx/admin-components';
import Api from '@barboxx/api';
import { Place } from '@barboxx/models';
import { Can, useBool } from '@barboxx/react-utils';

import { RootContainer } from '../../components/nav/RootContainer';
import { PlaceForm } from '../components/PlaceForm';
import { PLACE_SCHEMA } from '../components/PlaceForm/utils';
import {
    CurrentPlaceMap,
    CurrentPlaceMapRefProps,
} from './components/CurrentPlaceMap';
import { LinkBusiness } from './components/LinkBusiness';
import { PlaceDeleteCard } from './components/PlaceDeleteCard';

interface PlaceCardProps {
    place: Place;
    refetch: () => void;
}

function PlaceDetailsCard({ place, refetch }: PlaceCardProps) {
    const notifications = useNotifications();
    const [showMap, showMapHandlers] = useBool(false);
    const mapRef = useRef<CurrentPlaceMapRefProps | null>(null);

    const formContext = useForm<Place.UpdateRequest>({
        resolver: yupResolver(PLACE_SCHEMA),
        defaultValues: place,
    });

    const mutatePlace = useMutation({
        mutationKey: ['place', place.id],
        mutationFn: (data: Place.UpdateRequest) =>
            Api.Places.updatePlace(place.id, data),
    });

    const handleFormSave = (values: Place.UpdateRequest) =>
        mutatePlace
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show(
                    `The place "${data?.name}" has been updated successfully.`,
                    {
                        severity: 'success',
                    }
                );
                mapRef.current?.reset();
                refetch();
            })
            .catch(() => {
                notifications.show('Error updating place!', {
                    severity: 'error',
                });
            });

    const handleFormUpdates = (data: Place.UpdateRequest) => {
        formContext.reset(data);
    };

    return (
        <Stack gap={2} sx={{ paddingBottom: 4 }}>
            <Card>
                <CardHeader title="Basic Info" />

                <PlaceForm
                    onSubmit={handleFormSave}
                    formContext={formContext}
                />
            </Card>
            <Can I="manage" a="Place">
                <Card>
                    <Accordion
                        onChange={showMapHandlers.toggle}
                        expanded={showMap}
                        square={false}
                        TransitionProps={{ unmountOnExit: true }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMore />}
                            sx={{ paddingX: 3, minHeight: 64 }}
                        >
                            <Typography variant="h6">Find Business</Typography>
                        </AccordionSummary>
                        <AccordionDetails
                            sx={theme => ({
                                borderTop: `solid 1px ${theme.palette.divider}`,
                                paddingY: 2,
                            })}
                        >
                            <CurrentPlaceMap
                                updateFormData={handleFormUpdates}
                                currentPlace={place}
                                ref={mapRef}
                            />
                        </AccordionDetails>
                    </Accordion>
                </Card>
            </Can>
            <Can I="read" a="Business">
                <Card>
                    <CardHeader title="Business Link" />
                    <CardContent>
                        <LinkBusiness place={place} refreshData={refetch} />
                    </CardContent>
                </Card>
            </Can>
            {!place.business && (
                <Can I="delete" a="Business">
                    <PlaceDeleteCard place={place} />
                </Can>
            )}
        </Stack>
    );
}

export function PlaceDetails() {
    const { id } = useParams();

    const activePage = useActivePage();

    const breadcrumbs = [
        ...(activePage?.breadcrumbs ?? []),
        { title: 'Details', path: `${activePage?.path}/${id}` },
    ];

    const placeQueryResults = useQuery({
        queryKey: ['place', id],
        queryFn: () => Api.Places.getPlace(id as string),
    });

    return (
        <RootContainer breadcrumbs={breadcrumbs} title={'Place Details'}>
            <QueryRenderer results={placeQueryResults}>
                {(data, { refetch }) => (
                    <PlaceDetailsCard place={data} refetch={refetch} />
                )}
            </QueryRenderer>
        </RootContainer>
    );
}
